import {
  AllocationMethod,
  NeedsReviewReason,
  PayinStatus,
  PaymentInstructionStatus
} from '@sollapay/enums';

import {calculateAllocationConfidence, calculateAppliedAndExcessAmounts} from '../rules/index.js';

import type {PaymentInstructionEntity} from '../entities/payment-instruction.entity.js';

export interface PayinAllocationInput {
  instruction: PaymentInstructionEntity;
  /** The amount of the incoming payin */
  thisPayinAmount: string;
  /** Sum of prior ACCEPTED/PARTIALLY_ACCEPTED payins for this instruction */
  alreadyAllocatedAmount: string;
  /** Method used to allocate — REFERENCE_CODE today; extensibility seam for future methods */
  allocationMethod: AllocationMethod;
  confidence?: number;
}

interface PayinAllocationSettledVerdict {
  outcome: PayinStatus.ACCEPTED | PayinStatus.PARTIALLY_ACCEPTED;
  payinStatus: PayinStatus.ACCEPTED | PayinStatus.PARTIALLY_ACCEPTED;
  instructionStatus:
    | PaymentInstructionStatus.COMPLETED
    | PaymentInstructionStatus.PARTIALLY_COMPLETED;
  allocationMethod: AllocationMethod;
  confidence: number;
  needsReviewReason: null;
  /** The portion of thisPayinAmount that counts toward the instruction's remaining balance. */
  appliedAmount: string;
  /** The portion of thisPayinAmount beyond what the instruction still owes (an overpayment). */
  excessAmount: string;
}

interface PayinAllocationNeedsReviewVerdict {
  outcome: PayinStatus.NEEDS_REVIEW;
  payinStatus: PayinStatus.NEEDS_REVIEW;
  instructionStatus: null;
  allocationMethod: AllocationMethod;
  confidence: number;
  needsReviewReason: NeedsReviewReason;
  appliedAmount: null;
  excessAmount: null;
}

/**
 * Discriminated on `outcome` — only NEEDS_REVIEW ever has null appliedAmount/excessAmount, so
 * narrowing on `outcome` (e.g. `if (verdict.outcome === PayinStatus.NEEDS_REVIEW) return`) gives
 * callers a compiler-enforced guarantee that both amounts are real strings afterward, instead of
 * a defensive `?? amount` fallback that would silently misroute money if a future branch forgot
 * to set them.
 */
export type PayinAllocationVerdict =
  | PayinAllocationSettledVerdict
  | PayinAllocationNeedsReviewVerdict;

/** Allocation methods that identify the instruction too uncertainly to ever auto-settle. */
const UNCERTAIN_METHOD_REVIEW_REASON: Partial<Record<AllocationMethod, NeedsReviewReason>> = {
  [AllocationMethod.PARTIAL_ANAGRAM_REFERENCE_CODE]: NeedsReviewReason.LOW_CONFIDENCE_MATCH,
  [AllocationMethod.AI_MATCH]: NeedsReviewReason.AI_SUGGESTED_MATCH
};

export class PayinAllocationPolicy {
  static evaluate(input: PayinAllocationInput): PayinAllocationVerdict {
    const {instruction, thisPayinAmount, alreadyAllocatedAmount, allocationMethod} = input;
    const confidence = input.confidence ?? calculateAllocationConfidence(allocationMethod);

    const needsReviewReason = !instruction.canReceiveAllocation()
      ? NeedsReviewReason.INSTRUCTION_NOT_ALLOCATABLE
      : (UNCERTAIN_METHOD_REVIEW_REASON[allocationMethod] ?? null);

    if (needsReviewReason) {
      return {
        outcome: PayinStatus.NEEDS_REVIEW,
        payinStatus: PayinStatus.NEEDS_REVIEW,
        instructionStatus: null,
        allocationMethod,
        confidence,
        needsReviewReason,
        appliedAmount: null,
        excessAmount: null
      };
    }

    const {appliedAmount, excessAmount, isFullyPaid} = calculateAppliedAndExcessAmounts({
      instructionAmount: instruction.amount,
      alreadyAllocatedAmount,
      thisAmount: thisPayinAmount
    });

    const outcome = isFullyPaid ? PayinStatus.ACCEPTED : PayinStatus.PARTIALLY_ACCEPTED;
    const instructionStatus =
      outcome === PayinStatus.ACCEPTED
        ? PaymentInstructionStatus.COMPLETED
        : PaymentInstructionStatus.PARTIALLY_COMPLETED;

    return {
      outcome,
      payinStatus: outcome,
      instructionStatus,
      allocationMethod,
      confidence,
      needsReviewReason: null,
      appliedAmount,
      excessAmount
    };
  }
}
